import { useState, type FormEvent } from 'react';

import { Button, Card, Dropdown, Field, Input, MonoLabel } from '@/design-system';
import {
    MANUAL_BOOKING_DIRECTIONS,
    MANUAL_BOOKING_LABELS,
    PLATFORM_REASON_MIN_LENGTH,
    signedCredits,
    type ManualBookingDirection,
    type ManualBookingResult,
} from '@/entities/tenant';

import { adminCopy, formatCredits } from '../copy';

export interface CreditBookingFormProps {
    tenantName: string;
    actorName: string;
    /** Getekend: negatief is afboeken. */
    onBook: (input: { credits: number; reason: string }) => void;
    booking?: boolean;
    result?: ManualBookingResult | null;
    error?: string | null;
}

/**
 * Handmatige tegoedboeking door het platform, in hele credits.
 *
 * De reden is **verplicht** en het formulier laat een boeking zonder reden van
 * minstens tien tekens niet vertrekken. Dat elke boeking gelogd wordt, en door
 * wie, staat hier in beeld en niet in een handleiding: wie boekt moet weten dat
 * zijn naam eronder komt te staan.
 *
 * Er is geen "corrigeren" en geen "terugdraaien": het grootboek is append-only,
 * dus een fout herstel je met een tegenboeking in de andere richting.
 */
export function CreditBookingForm({
    tenantName,
    actorName,
    onBook,
    booking = false,
    result = null,
    error = null,
}: CreditBookingFormProps) {
    const [direction, setDirection] = useState<ManualBookingDirection>('credit');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');

    const credits = /^\d+$/.test(amount.trim()) ? Number(amount.trim()) : 0;
    const creditsValid = credits > 0;
    const reasonValid = reason.trim().length >= PLATFORM_REASON_MIN_LENGTH;

    function submit(event: FormEvent) {
        event.preventDefault();

        if (!creditsValid || !reasonValid) {
            return;
        }

        onBook({ credits: signedCredits(direction, credits), reason: reason.trim() });
        setAmount('');
        setReason('');
    }

    return (
        <Card padding="md" as="section" aria-labelledby="booking-title">
            <h2
                id="booking-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {adminCopy.bookingTitle}
            </h2>
            <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {adminCopy.bookingIntro(tenantName)}
            </p>

            <form onSubmit={submit} className="mt-3 grid gap-[10px]">
                <Field label={adminCopy.bookingDirection} size="sm">
                    <Dropdown
                        value={direction}
                        options={MANUAL_BOOKING_DIRECTIONS.map((option) => ({
                            value: option,
                            label: MANUAL_BOOKING_LABELS[option],
                        }))}
                        onChange={setDirection}
                    />
                </Field>

                <Field
                    label={adminCopy.bookingCredits}
                    size="sm"
                    description={adminCopy.bookingCreditsHint}
                    error={
                        amount !== '' && !creditsValid ? adminCopy.bookingCreditsInvalid : undefined
                    }
                >
                    <Input
                        mono
                        inputMode="numeric"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                    />
                </Field>

                <Field
                    label={adminCopy.reason}
                    size="sm"
                    description={adminCopy.impersonateReasonHint}
                    error={
                        reason !== '' && !reasonValid
                            ? adminCopy.reasonTooShort(PLATFORM_REASON_MIN_LENGTH)
                            : undefined
                    }
                >
                    <Input
                        value={reason}
                        placeholder={adminCopy.bookingReasonPlaceholder}
                        onChange={(event) => setReason(event.target.value)}
                    />
                </Field>

                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={booking || !creditsValid || !reasonValid}
                    >
                        {adminCopy.bookingSubmit}
                    </Button>
                    <MonoLabel size={10} tone="muted">
                        {adminCopy.bookingLoggedAs(actorName)}
                    </MonoLabel>
                </div>
            </form>

            {result ? (
                <p
                    role="status"
                    className="m-0 mt-3 text-[12px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {result.replayed
                        ? adminCopy.bookingReplayed(formatCredits(result.balanceAfter))
                        : adminCopy.bookingDone(
                              formatCredits(result.credits, { signed: true }),
                              formatCredits(result.balanceAfter),
                          )}
                </p>
            ) : null}

            {error ? (
                <p role="alert" className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}
        </Card>
    );
}
